import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function RedeemCodeDialog({ open, onOpenChange, onRedeemed, initialCode = "" }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [etsyUrl, setEtsyUrl] = useState("");
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!open) return;
    if (initialCode) setCode(initialCode);
    api.get("/settings").then((r) => setEtsyUrl(r.data.etsy_url || "")).catch(() => {});
  }, [open, initialCode]);

  const submit = async () => {
    if (!code.trim()) return toast.error("Enter a code");
    setLoading(true);
    try {
      await api.post("/codes/redeem", { code: code.trim() });
      await refreshUser();
      toast.success("Credit added to your account");
      setCode("");
      onOpenChange(false);
      onRedeemed && onRedeemed();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="redeem-dialog" className="rounded-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl font-light tracking-tight">
            Redeem a code
          </DialogTitle>
          <p className="text-sm text-neutral-600 pt-1">
            Enter the Soirée code from your Etsy purchase or gift card. Each code adds one credit to publish an invite.
          </p>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] tracking-[0.25em] uppercase">Code</Label>
            <Input
              data-testid="redeem-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="SOIREE-XXXX-XXXX"
              className="rounded-sm mt-1 font-mono tracking-wider"
              autoFocus
            />
          </div>
          {etsyUrl && (
            <p className="text-xs text-neutral-500">
              Don&apos;t have a code?{" "}
              <a data-testid="redeem-etsy-link" href={etsyUrl} target="_blank" rel="noreferrer" className="underline underline-offset-4 text-neutral-800">
                Buy one on Etsy ↗
              </a>
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="redeem-submit" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
            {loading ? "Redeeming…" : "Redeem"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
